/*
 * Visor de catálogos DKo Home — catalogo-visor.js
 *
 * Une pdf.js (lee y dibuja el PDF) con page-flip (efecto de pasar la página).
 * No contiene ningún valor configurable: todo llega por atributos data- desde
 * sections/catalogo-visor.liquid, que a su vez los toma de los ajustes de la
 * sección en el editor del tema.
 *
 * Dependencias (cargadas antes por la sección, en este orden):
 *   assets/pdfjs-3.11.174.min.js         pdf.js 3.11.174 (último build UMD, ver INSTALACION.md)
 *   assets/page-flip-2.0.7.browser.js  page-flip 2.0.7 (global window.St)
 *
 * Cómo funciona, en corto:
 *   1. Abre el PDF en streaming (range requests) y lee el número de páginas.
 *   2. Crea una hoja vacía por página y se las entrega a page-flip.
 *   3. Dibuja solo las páginas cercanas a la que se mira (ventana de ±2) y
 *      libera las lejanas, para que un catálogo largo no agote la memoria.
 *   4. Sobre cada página dibujada pone una capa con los enlaces del PDF:
 *      los externos abren en pestaña nueva, los internos saltan a la página.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-catalogo-visor]';
  var VENTANA_ATRAS = 2;     // páginas dibujadas antes de la visible
  var VENTANA_ADELANTE = 3;  // páginas dibujadas después de la visible
  var ANCHO_MAX_RENDER = 1600; // px reales máximos por página (calidad vs. memoria)

  function iniciar(root) {
    if (root.dataset.visorIniciado === '1') return;
    root.dataset.visorIniciado = '1';

    var cfg = {
      pdfUrl: root.dataset.pdfUrl,
      workerSrc: root.dataset.workerSrc,
      modo: root.dataset.modo === 'simple' ? 'simple' : 'doble',
      sombra: clamp(parseInt(root.dataset.sombra, 10), 0, 100, 50) / 100,
      velocidad: clamp(parseInt(root.dataset.velocidad, 10), 200, 3000, 900),
      anchoMax: clamp(parseInt(root.dataset.anchoMax, 10), 400, 2400, 1100)
    };

    var el = {
      estado: root.querySelector('[data-estado]'),
      libro: root.querySelector('[data-libro]'),
      controles: root.querySelector('[data-controles]'),
      anterior: root.querySelector('[data-anterior]'),
      siguiente: root.querySelector('[data-siguiente]'),
      contador: root.querySelector('[data-contador]'),
      pantalla: root.querySelector('[data-pantalla-completa]')
    };

    if (!cfg.pdfUrl) return fallar(root, el, 'Este catálogo no tiene un PDF asociado.');
    if (!window.pdfjsLib || !window.St) return fallar(root, el, 'No se pudo cargar el visor.');

    window.pdfjsLib.GlobalWorkerOptions.workerSrc = cfg.workerSrc;

    var pdf = null;
    var pageFlip = null;
    var hojas = [];
    var ratio = 1.4;             // alto / ancho de la página, se lee del PDF
    var dibujadas = {};          // nº página (1-based) -> ancho CSS con que se dibujó
    var dibujando = {};
    var destruido = false;

    estado('Cargando catálogo…');

    abrirPdf(cfg.pdfUrl, false)
      .catch(function (err) {
        // Algunos navegadores bloquean el worker; se reintenta una vez sin él.
        console.warn('[catalogo-visor] reintento sin worker:', err && err.message);
        return abrirPdf(cfg.pdfUrl, true);
      })
      .then(function (doc) {
        pdf = doc;
        return pdf.getPage(1);
      })
      .then(function (primera) {
        var vp = primera.getViewport({ scale: 1 });
        ratio = vp.height / vp.width;
        primera.cleanup();
        construirLibro(pdf.numPages);
      })
      .catch(function (err) {
        console.error('[catalogo-visor]', err);
        fallar(root, el, 'No se pudo abrir el catálogo. Puedes descargarlo con el enlace de abajo.');
      });

    function abrirPdf(url, sinWorker) {
      var tarea = window.pdfjsLib.getDocument({
        url: url,
        disableAutoFetch: true,   // baja solo los trozos que necesita (range requests)
        disableStream: false,
        rangeChunkSize: 65536,
        disableWorker: !!sinWorker
      });
      tarea.onProgress = function (p) {
        if (!pdf && p.total) {
          estado('Cargando catálogo… ' + Math.min(100, Math.round(p.loaded / p.total * 100)) + '%');
        }
      };
      return tarea.promise;
    }

    function construirLibro(total) {
      if (destruido) return;
      el.libro.innerHTML = '';
      hojas = [];
      for (var i = 1; i <= total; i++) {
        var hoja = document.createElement('div');
        hoja.className = 'cv-hoja';
        // Portada y contraportada se comportan como tapa dura.
        if (i === 1 || i === total) hoja.setAttribute('data-density', 'hard');
        var canvas = document.createElement('canvas');
        canvas.className = 'cv-hoja__canvas';
        var num = document.createElement('span');
        num.className = 'cv-hoja__numero';
        num.textContent = String(i);
        var enlaces = document.createElement('div');
        enlaces.className = 'cv-hoja__enlaces';
        hoja.appendChild(canvas);
        hoja.appendChild(num);
        hoja.appendChild(enlaces);
        el.libro.appendChild(hoja);
        hojas.push(hoja);
      }

      var anchoPagina = 500;
      var simple = cfg.modo === 'simple';

      ajustarAltoLibro();

      pageFlip = new window.St.PageFlip(el.libro, {
        width: anchoPagina,
        height: Math.round(anchoPagina * ratio),
        size: 'stretch',
        // En modo "simple" se fuerza retrato poniendo un minWidth imposible de
        // superar; page-flip solo cambia a doble página cuando el contenedor
        // mide más de 2 × minWidth. El estilo que deja en el contenedor se
        // limpia justo después (ver más abajo).
        minWidth: simple ? 100000 : 200,
        maxWidth: simple ? cfg.anchoMax : Math.round(cfg.anchoMax / 2),
        minHeight: 200,
        maxHeight: Math.round(cfg.anchoMax * ratio),
        usePortrait: true,
        autoSize: true,
        showCover: true,
        drawShadow: cfg.sombra > 0,
        maxShadowOpacity: cfg.sombra,
        flippingTime: cfg.velocidad,
        mobileScrollSupport: true,
        showPageCorners: true,
        useMouseEvents: true
      });
      pageFlip.loadFromHTML(hojas);

      // page-flip escribe min-width/max-width en el contenedor al cargar las
      // hojas; se limpian para que mande el CSS (y el minWidth "imposible" del
      // modo simple no ensanche nada).
      el.libro.style.minWidth = '';
      el.libro.style.minHeight = '';
      el.libro.style.maxWidth = '';
      ajustarAltoLibro();
      pageFlip.update();

      pageFlip.on('flip', function () { actualizarVentana(); });
      pageFlip.on('changeOrientation', function () {
        ajustarAltoLibro();
        redibujarTodo();
      });

      estado('');
      root.classList.add('cv--listo');
      if (el.controles) el.controles.hidden = false;

      conectarControles();
      actualizarVentana();

      var temporizador = null;
      window.addEventListener('resize', function () {
        clearTimeout(temporizador);
        temporizador = setTimeout(function () {
          ajustarAltoLibro();
          if (pageFlip) pageFlip.update();
          redibujarSiCambioTamano();
        }, 250);
      });
    }

    // page-flip en modo "stretch" necesita que el contenedor tenga alto. Se
    // calcula desde el ancho disponible y la proporción real de la página.
    function ajustarAltoLibro() {
      var ancho = el.libro.clientWidth || root.clientWidth || 600;
      var retrato = cfg.modo === 'simple' || ancho < 400;
      var anchoPag = retrato ? ancho : ancho / 2;
      var tope = retrato ? cfg.anchoMax : cfg.anchoMax / 2;
      if (anchoPag > tope) anchoPag = tope;
      var alto = Math.round(anchoPag * ratio);
      // Nunca más alto que la ventana: page-flip reduce el ancho de página
      // para que el libro completo quepa en pantalla.
      var margen = root.classList.contains('cv--pantalla-completa') ? 32 : 120;
      var altoVentana = Math.max(240, window.innerHeight - margen);
      if (alto > altoVentana) alto = altoVentana;
      el.libro.style.height = alto + 'px';
    }

    function paginasVisibles() {
      // page-flip entrega el índice (0-based) de la primera página del pliego.
      // Con tapa (showCover) los pliegos en doble página son [0], [1,2], [3,4]…
      // y, si el total es par, la contraportada queda sola al final.
      var idx = pageFlip.getCurrentPageIndex();
      var retrato = pageFlip.getOrientation() === 'portrait';
      var lista = [idx + 1];
      if (!retrato && idx > 0 && idx + 2 <= hojas.length) lista.push(idx + 2);
      return lista;
    }

    function actualizarVentana() {
      if (!pageFlip || destruido) return;
      var visibles = paginasVisibles();
      var desde = Math.max(1, visibles[0] - VENTANA_ATRAS);
      var hasta = Math.min(hojas.length, visibles[visibles.length - 1] + VENTANA_ADELANTE);

      Object.keys(dibujadas).forEach(function (k) {
        var n = parseInt(k, 10);
        if (n < desde || n > hasta) liberar(n);
      });

      actualizarContador(visibles);

      // Primero lo visible, después los vecinos.
      var cola = visibles.slice();
      for (var n = desde; n <= hasta; n++) if (visibles.indexOf(n) === -1) cola.push(n);
      cola.reduce(function (p, n) { return p.then(function () { return dibujar(n); }); }, Promise.resolve());
    }

    function dibujar(n) {
      if (destruido || dibujadas[n] || dibujando[n]) return Promise.resolve();
      dibujando[n] = true;
      var hoja = hojas[n - 1];
      var canvas = hoja.querySelector('canvas');
      return pdf.getPage(n).then(function (page) {
        var anchoCss = hoja.clientWidth || 500;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var anchoReal = Math.min(anchoCss * dpr, ANCHO_MAX_RENDER);
        var vp1 = page.getViewport({ scale: 1 });
        var vp = page.getViewport({ scale: anchoReal / vp1.width });
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        return page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise.then(function () {
          dibujadas[n] = anchoCss;
          hoja.classList.add('cv-hoja--lista');
          return pintarEnlaces(page, vp, hoja);
        }).then(function () {
          page.cleanup();
        });
      }).catch(function (err) {
        console.error('[catalogo-visor] página ' + n, err);
        hoja.classList.add('cv-hoja--error');
      }).then(function () {
        delete dibujando[n];
      });
    }

    function liberar(n) {
      var canvas = hojas[n - 1].querySelector('canvas');
      canvas.width = 0;
      canvas.height = 0;
      hojas[n - 1].classList.remove('cv-hoja--lista');
      hojas[n - 1].querySelector('.cv-hoja__enlaces').innerHTML = '';
      delete dibujadas[n];
    }

    // Capa de enlaces: un <a> por cada anotación de tipo Link del PDF, ubicado
    // en porcentaje sobre la página para que acompañe cualquier tamaño.
    function pintarEnlaces(page, vp, hoja) {
      var capa = hoja.querySelector('.cv-hoja__enlaces');
      capa.innerHTML = '';
      return page.getAnnotations({ intent: 'display' }).then(function (anots) {
        anots.forEach(function (a) {
          if (a.subtype !== 'Link' || (!a.url && !a.dest)) return;
          var r = vp.convertToViewportRectangle(a.rect);
          var x1 = Math.min(r[0], r[2]), x2 = Math.max(r[0], r[2]);
          var y1 = Math.min(r[1], r[3]), y2 = Math.max(r[1], r[3]);
          var el = document.createElement('a');
          el.className = 'cv-enlace';
          el.style.left = (x1 / vp.width * 100) + '%';
          el.style.top = (y1 / vp.height * 100) + '%';
          el.style.width = ((x2 - x1) / vp.width * 100) + '%';
          el.style.height = ((y2 - y1) / vp.height * 100) + '%';
          if (a.url) {
            el.href = a.url;
            el.target = '_blank';
            el.rel = 'noopener';
            el.title = a.url;
          } else {
            el.href = '#';
            el.setAttribute('aria-label', 'Ir a otra página del catálogo');
            el.addEventListener('click', function (e) {
              e.preventDefault();
              irADestino(a.dest);
            });
          }
          // Que el clic en el enlace no inicie un paso de página en page-flip.
          ['mousedown', 'touchstart', 'pointerdown'].forEach(function (ev) {
            el.addEventListener(ev, function (e) { e.stopPropagation(); }, { passive: true });
          });
          capa.appendChild(el);
        });
      }).catch(function (err) {
        console.warn('[catalogo-visor] enlaces', err);
      });
    }

    function irADestino(dest) {
      var p = typeof dest === 'string' ? pdf.getDestination(dest) : Promise.resolve(dest);
      p.then(function (d) {
        if (!d || !d[0]) return;
        return pdf.getPageIndex(d[0]);
      }).then(function (idx) {
        if (typeof idx === 'number' && pageFlip) pageFlip.flip(idx);
      }).catch(function (err) {
        console.warn('[catalogo-visor] destino interno', err);
      });
    }

    function redibujarTodo() {
      Object.keys(dibujadas).forEach(function (k) { liberar(parseInt(k, 10)); });
      actualizarVentana();
    }

    function redibujarSiCambioTamano() {
      var muestra = Object.keys(dibujadas)[0];
      if (!muestra) return;
      var anchoAhora = hojas[parseInt(muestra, 10) - 1].clientWidth;
      var anchoAntes = dibujadas[muestra];
      if (Math.abs(anchoAhora - anchoAntes) / anchoAntes > 0.2) redibujarTodo();
    }

    function actualizarContador(visibles) {
      if (!el.contador) return;
      el.contador.textContent = visibles.join('–') + ' / ' + hojas.length;
      if (el.anterior) el.anterior.disabled = visibles[0] <= 1;
      if (el.siguiente) el.siguiente.disabled = visibles[visibles.length - 1] >= hojas.length;
    }

    function conectarControles() {
      if (el.anterior) el.anterior.addEventListener('click', function () { pageFlip.flipPrev(); });
      if (el.siguiente) el.siguiente.addEventListener('click', function () { pageFlip.flipNext(); });

      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); pageFlip.flipPrev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); pageFlip.flipNext(); }
        if (e.key === 'Escape' && root.classList.contains('cv--pantalla-completa')) salirPantallaCompleta();
      });

      if (el.pantalla) {
        el.pantalla.addEventListener('click', function () {
          if (document.fullscreenElement === root || root.classList.contains('cv--pantalla-completa')) {
            salirPantallaCompleta();
          } else {
            entrarPantallaCompleta();
          }
        });
        document.addEventListener('fullscreenchange', function () {
          root.classList.toggle('cv--pantalla-completa', document.fullscreenElement === root);
          trasCambioPantalla();
        });
      }
    }

    function entrarPantallaCompleta() {
      if (root.requestFullscreen) {
        root.requestFullscreen().catch(function () { modoCapa(true); });
      } else {
        modoCapa(true); // Safari iOS no tiene API de pantalla completa para divs
      }
    }

    function salirPantallaCompleta() {
      if (document.fullscreenElement === root && document.exitFullscreen) {
        document.exitFullscreen();
      } else {
        modoCapa(false);
      }
    }

    function modoCapa(activar) {
      root.classList.toggle('cv--pantalla-completa', activar);
      document.documentElement.classList.toggle('cv-bloquea-scroll', activar);
      trasCambioPantalla();
    }

    function trasCambioPantalla() {
      ajustarAltoLibro();
      if (pageFlip) {
        pageFlip.update();
        setTimeout(redibujarTodo, 50);
      }
      root.focus();
    }

    function estado(texto) {
      if (!el.estado) return;
      el.estado.textContent = texto;
      el.estado.hidden = !texto;
    }
  }

  function fallar(root, el, mensaje) {
    root.classList.add('cv--error');
    if (el.estado) { el.estado.textContent = mensaje; el.estado.hidden = false; }
    if (el.libro) el.libro.hidden = true;
    if (el.controles) el.controles.hidden = true;
    var descarga = root.querySelector('[data-descarga]');
    if (descarga) descarga.hidden = false;
  }

  function clamp(v, min, max, porDefecto) {
    if (isNaN(v)) return porDefecto;
    return Math.min(max, Math.max(min, v));
  }

  function iniciarTodos(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(iniciar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { iniciarTodos(); });
  } else {
    iniciarTodos();
  }

  // Editor del tema: la sección se vuelve a renderizar al cambiar un ajuste.
  document.addEventListener('shopify:section:load', function (e) { iniciarTodos(e.target); });
})();
